import './index.scss';

import { Component, Fragment, ReactNode } from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { BraxButton, BraxModal, BraxModals, BraxModalsType, BraxToasts, BraxToastsType } from 'design-system';
import { CreateCustomModalNode } from 'design-system/dist/components/BraxModals/context';
import { CreateToastProps } from 'design-system/dist/components/BraxToasts/context';

import t from '../../utils/i18n';
import { withContexts } from '../../utils/with-context';
import User from '../../view-model/users/User';
import UserContext from '../../contexts/User';
import router from '../../routers';
import Plans from '../../pages/plans/Plans';
import AppBarTourOnboarding from '../AppBarTourOnboarding';
import RegistrationAddon from '../RegistrationAddon';
import PoliciesUpdate from '../PoliciesUpdate';
import TopBarNotices from '../TopBarNotices';

export type NoticesProps = RouteComponentProps & {
    modals: BraxModalsType,
    toasts: BraxToastsType,
    user: User
};

type NoticesState = {
    childrens: { render: () => ReactNode, notice: Notice }[]
};

type Notice = {
    closeable: boolean,
    autoClose: boolean,
    closed?: boolean,
    condition?: () => boolean,
    routeCondition?: () => boolean,
    onClose?: () => void
} & ({
    type: 'children',
    permanent: boolean,
    content: (onClose: () => void) => ReactNode
} | {
    type: 'modal',
    content: (onClose: () => void, close: () => void) => CreateCustomModalNode
} | {
    type: 'toast',
    content: (dismiss: () => void) => CreateToastProps
});

export default withRouter(withContexts({
    modals: BraxModals,
    toasts: BraxToasts,
    user: UserContext
}, class Notices extends Component<NoticesProps, NoticesState> {

    state: NoticesState = {
        childrens: []
    };

    notices: Notice[] = [{
        closeable: true,
        autoClose: true,
        permanent: true,
        type: 'children',
        routeCondition: () => !this.inUnauthenticatedRoute(),
        content: () => <TopBarNotices />
    }, {
        closeable: true,
        autoClose: false,
        type: 'modal',
        condition: () => !this.props.user.sector && !this.props.user.activity,
        routeCondition: () => !this.inUnauthenticatedRoute(),
        content: onClose => <RegistrationAddon onClose={onClose} />
    }, {
        closeable: true,
        autoClose: false,
        type: 'modal',
        condition: () => this.props.user.needAcceptPolicies,
        routeCondition: () => !this.inUnauthenticatedRoute(),
        content: onClose => <PoliciesUpdate onClose={onClose} />
    }, {
        closeable: false,
        autoClose: false,
        type: 'modal',
        condition: () => this.props.user.hasPeddingRestration,
        routeCondition: () => !this.inUnauthenticatedRoute() && !this.isRoute('/user') && !this.isRoute('/notifications'),
        content: (onClose, close) => (
            <BraxModal className="user-pendding-registration" close={false} backdrop={false} onClose={async () => {
                this.redirect('/user');
                // Espera aplicar as alterações na rota atual para ir ao próximo aviso 
                await void 0;
                onClose();
            }}>
                <h2>{ t('Aviso') }</h2>

                <p>{ t('Você precisa atualizar seus dados cadastrais.') }</p>

                <BraxButton variant="primary" size="md" onClick={close}>{ t('Corrigir') }</BraxButton>
            </BraxModal>
        )
    }, {
        closeable: true,
        autoClose: false,
        permanent: false,
        type: 'children',
        routeCondition: () => this.isRoute('/projects'),
        content: onClose => <AppBarTourOnboarding onEnd={onClose} />
    }, {
        closeable: true,
        autoClose: true,
        type: 'toast',
        condition: () => this.props.user.plan.isFree && !this.props.user.subscription?.isActive,
        routeCondition: () => !this.inUnauthenticatedRoute() && !this.isRoute('/editor'),
        content: (dismiss) => ({
            type: 'primary',
            title: t('Planos, Funcionalidades e Preços'),
            description: t('Para desfrutar ainda mais do MeuPasseioVirtual, faça a assinatura de um plano e tenha acesso a funcionalidades exclusivas.'),
            action: t('Ver planos'),
            onClick: () => dismiss(),
            onAction: () => this.props.modals.custom(<Plans />)
        })
    }];

    index = 0;

    componentDidMount() {
        this.checkNotices();
    }

    componentDidUpdate(prevProps: NoticesProps) {
        if (prevProps.user.id !== this.props.user.id) {
            this.notices = this.notices.map(notice => {
                if ('closed' in notice)
                    notice.closed = false;

                return notice;
            });

            this.checkNotices();
        } else if (prevProps.location.key !== this.props.location.key) {
            this.checkNotices();
        }
    }

    checkNotices() {
        this.index = 0;

        this.checkNextNotice();
    }

    checkNextNotice() {
        const notice = this.notices[this.index++];

        if (!notice)
            return;

        if (notice.closed) {
            this.checkNextNotice();
        } else if (notice.condition && !notice.condition()) {
            this.close(notice);
        } else if (notice.routeCondition && !notice.routeCondition()) {
            this.checkNextNotice();
        } else {
            if (notice.type === 'modal')
                var close = this.props.modals.custom(notice.content(() => this.close(notice), () => close()));
            else if (notice.type === 'toast')
                var dismiss = this.props.toasts.create(notice.content(() => dismiss()));
            else
                this.setState(prevState => ({
                    childrens: [...prevState.childrens, { render: () => notice.content(() => this.close(notice)), notice }]
                }));

            if (notice.autoClose)
                this.close(notice);
        }
    }

    close(notice: Notice) {
        if (notice.closeable) {
            if (notice.type === 'children' && !notice.permanent)
                this.setState(preState => ({ childrens: preState.childrens.filter(children => children.notice !== notice) }));

            notice.closed = true;
        }

        this.checkNextNotice();
    }

    isRoute(path: string) {
        return this.props.location.pathname.startsWith(path);
    }

    inUnauthenticatedRoute() {
        return router.unauthenticated.findIndex(route => route.path && this.isRoute(route.path)) !== -1;
    }

    redirect(path: string) {
        this.props.history.push(path);
    }

    render() {
        return this.state.childrens.map((children, index) => <Fragment key={index}>{ children.render() }</Fragment>);
    }

}));
